import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Lock, RotateCcw } from 'lucide-react';
import { Button } from '../actions/Button';
import { SmallSelect } from '../inputs/SmallSelect';
import { usePlans, usePurchaseInvite } from '../../api/hooks/useBilling';
import { track } from '../../utils/analytics';
import { trackEvent } from '../../utils/trackEvent';
import { CURRENCIES, CURRENCY_SELECT_OPTIONS } from '../../utils/currencies';
import type { PublishGateError } from '../../types';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  error: PublishGateError | null;
  inviteId?: number;
  inviteTitle?: string;
  headline?: string;
  isPaid?: boolean;
}

export function UpgradeModal({ isOpen, onClose, error, inviteId, inviteTitle, headline: _headline, isPaid = false }: UpgradeModalProps) {
  const { data: plans } = usePlans();
  const purchaseInvite = usePurchaseInvite();
  const [currency, setCurrency] = useState(0); // index into CURRENCIES

  // Track first publish attempt per modal open (skip if already paid)
  useEffect(() => {
    if (isOpen && error && !isPaid) {
      trackEvent('publish_attempted', { invite_id: inviteId, invite_title: inviteTitle });
    }
  }, [isOpen, error, inviteId, inviteTitle, isPaid]);

  // Track when modal closes without payment — skip if already paid
  useEffect(() => {
    if (!isOpen) return;
    return () => {
      if (!isPaid && !purchaseInvite.isSuccess) {
        trackEvent('payment_modal_abandoned', { invite_id: inviteId, invite_title: inviteTitle });
      }
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!error) return null;

  const perInvite = plans?.products?.per_invite;
  const promoActive = plans?.promo_active ?? false;

  const displayPrice = perInvite?.price;
  const originalPrice = null;

  const handlePayAndPublish = () => {
    if (!inviteId) return;
    track('payment_cta_clicked', { invite_id: inviteId, price: displayPrice, promo: promoActive });
    purchaseInvite.mutate({ inviteId, product: 'per_invite' });
  };

  const isLoading = purchaseInvite.isPending;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-background-card rounded-2xl shadow-xl z-50 flex flex-col max-h-[90vh] overflow-hidden"
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-background flex items-center justify-center text-text-secondary z-10"
            >
              <X size={18} />
            </button>

            {/* Hero price area */}
            <div className="px-6 pt-6 pb-6 text-center border-b border-border">
              {/* Currency selector — top left */}
              <div className="flex justify-start mb-3">
                <SmallSelect
                  value={CURRENCIES[currency].code}
                  onChange={(val) => setCurrency(CURRENCIES.findIndex(c => c.code === val))}
                  options={CURRENCY_SELECT_OPTIONS}
                />
              </div>

              {/* Price */}
              {perInvite && (
                <div className="flex items-end justify-center gap-2 mb-1">
                  <span className="text-5xl font-bold text-text-primary">
                    {CURRENCIES[currency].symbol}{CURRENCIES[currency].perInvite}
                  </span>
                </div>
              )}

              {/* One-time badge */}
              <div className="flex items-center justify-center mt-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-success/10 text-success px-3 py-1 rounded-full">
                  <Check size={11} strokeWidth={2.5} />
                  One Payment · Yours Forever
                </span>
              </div>
              {currency !== 0 && (
                <p className="text-[10px] text-text-tertiary mt-2">
                  Indicative price · Stripe converts £39 to {CURRENCIES[currency].code} at the live rate
                </p>
              )}

            </div>

            {/* Features + CTA */}
            <div className="px-6 py-5">
              <div className="space-y-2 mb-5">
                {['Save the Date included', 'Unlimited guests', 'RSVP tracking & reminders', 'Send via email, link or QR code', 'Plus-one & dietary controls', '14 languages supported'].map((f) => (
                  <div key={f} className="flex items-center gap-2.5 text-sm text-text-secondary">
                    <Check size={13} className="text-success flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>

              <Button
                fullWidth
                variant="primary"
                onClick={handlePayAndPublish}
                disabled={isLoading || !inviteId}
                icon={<Lock size={14} />}
              >
                {purchaseInvite.isPending ? 'Processing…' : 'Continue to Payment'}
              </Button>

              {/* Trust badge */}
              <div className="flex items-center justify-center mt-4">
                <span className="inline-flex items-center gap-1.5 text-[11px] text-text-secondary">
                  <RotateCcw size={11} className="text-text-tertiary" />
                  14-day refund policy
                </span>
              </div>

              <p className="text-[11px] text-text-faint text-center mt-3">
                By continuing you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-text-muted transition-colors">
                  Terms of Service
                </a>
              </p>
            </div>

          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
